"use strict";
import { chainIdToBackendChain } from "constants/chains";
import { NATIVE_CHAIN_ID, nativeOnChain } from "constants/tokens";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { MAX_RECENT_SEARCH_RESULTS } from "uniswap/src/components/TokenSelector/hooks";
import {
  useRecentlySearchedAssetsQuery
} from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import {
  isNFTCollectionSearchResult,
  isTokenSearchResult
} from "uniswap/src/features/search/SearchResult";
import { selectSearchHistory } from "uniswap/src/features/search/selectSearchHistory";
import { isNativeCurrencyAddress } from "uniswap/src/utils/currencyId";
import { logger } from "utilities/src/logger/logger";
export function useRecentlySearchedAssets() {
  const history = useSelector(selectSearchHistory);
  const shortenedHistory = useMemo(() => history.slice(0, MAX_RECENT_SEARCH_RESULTS), [history]);
  const { data: queryData, loading } = useRecentlySearchedAssetsQuery({
    variables: {
      collectionAddresses: shortenedHistory.filter(isNFTCollectionSearchResult).map((asset) => asset.address),
      contracts: shortenedHistory.filter(isTokenSearchResult).map((token) => ({
        address: token.address ?? void 0,
        chain: chainIdToBackendChain({ chainId: token.chainId })
      }))
    },
    skip: shortenedHistory.length === 0
  });
  const data = useMemo(() => {
    if (shortenedHistory.length === 0) {
      return [];
    } else if (!queryData) {
      return void 0;
    }
    const resultsMap = {};
    const queryCollections = queryData?.nftCollections?.edges.map((edge) => edge.node);
    const collections = queryCollections?.map(
      (queryCollection) => {
        return {
          address: queryCollection.nftContracts?.[0]?.address ?? "",
          isVerified: queryCollection?.isVerified,
          name: queryCollection?.name,
          stats: {
            floor_price: queryCollection?.markets?.[0]?.floorPrice?.value,
            total_supply: queryCollection?.numAssets
          },
          imageUrl: queryCollection?.image?.url ?? ""
        };
      },
      [queryCollections]
    );
    collections?.forEach((collection) => resultsMap[collection.address] = collection);
    queryData.tokens?.filter(Boolean).forEach((token) => {
      if (token) {
        resultsMap[token.address ?? getNativeQueryAddress(token.chain)] = token;
      }
    });
    const data2 = [];
    shortenedHistory.forEach((asset) => {
      const result = generateInterfaceHistoryItem(asset, resultsMap);
      if (result) {
        data2.push(result);
      }
    });
    return data2;
  }, [queryData, shortenedHistory]);
  return { data, loading };
}
function generateInterfaceHistoryItem(asset, resultsMap) {
  if (isNFTCollectionSearchResult(asset)) {
    return resultsMap[asset.address];
  }
  if (!isTokenSearchResult(asset)) {
    return void 0;
  }
  if (!isNativeCurrencyAddress(asset.chainId, asset.address) && asset.address) {
    return resultsMap[asset.address];
  }
  if (isNativeCurrencyAddress(asset.chainId, asset.address)) {
    const chain = chainIdToBackendChain({ chainId: asset.chainId });
    if (!chain) {
      logger.error(new Error("Invalid chain retrieved from Search Token/Collection Query"), {
        tags: {
          file: "RecentlySearchedAssets",
          function: "useRecentlySearchedAssets"
        },
        extra: { asset }
      });
      return void 0;
    }
    const native = nativeOnChain(asset.chainId);
    const queryAddress = asset.address ?? getNativeQueryAddress(chain);
    const result = resultsMap[queryAddress];
    return { ...result, address: NATIVE_CHAIN_ID, ...native };
  }
  return void 0;
}
function getNativeQueryAddress(chain) {
  return `NATIVE-${chain}`;
}
